import { Injectable } from '@angular/core';
import { Tooth } from '@modules/teeth-diagram/models/tooth';
import { ValueOf } from '@shared/utils/type-util';
import { RxMaterialRulesService } from '@shared/services/rx-rules-helper/rx-material-rules.service';
import { OrderQuery } from '@modules/order/state/order-query';
import { memoize } from '@shared/utils/memoize-decorator';
import { UnitTypesInBridge } from '@modules/teeth-diagram/models/unit-type-in-bridge.enum';
import { MaterialOption } from '@shared/models/material-option';
import { ProcedureMapCheckedValue } from '@core/procedure-helpers/models/procedure-map-composites';
import { Maybe } from '@/types/globals';

@Injectable({
	providedIn: 'root'
})
export class ValidateToothPropertyService {
	constructor(
		private readonly rxMaterialRulesService: RxMaterialRulesService,
		private readonly orderQuery: OrderQuery
	) {}

	validatePropsCompatibilityInTooth(tooth: Tooth, propName: keyof Tooth, value: ValueOf<Tooth>): boolean {
		if (value == null) {
			return false;
		}
		switch (propName) {
			case 'SpecificationId':
				return this.validateSpecification(tooth, value);
			case 'MaterialID':
				return this.validateMaterial(tooth, value);
			case 'ShadeBody':
				return this.validateBody(tooth);
			default:
				return true;
		}
	}

	private validateSpecification(tooth: Tooth, value: ValueOf<Tooth>): boolean {
		const specWithMaterial = this.getSpecIdsWithMaterialIdsForBridge(this.orderQuery.procedureMap, tooth.ToothInBridgeTypeID as any);

		return specWithMaterial.some(spec => spec.Id === value);
	}
	private validateMaterial(tooth: Tooth, value: ValueOf<Tooth>): boolean {
		const specWithMaterial = this.getSpecIdsWithMaterialIdsForBridge(this.orderQuery.procedureMap, tooth.ToothInBridgeTypeID as any);
		// validation requires the tooth to have specificationId. Make sure it has been copied or defined
		const currentSpecWithMat = specWithMaterial?.find(spec => spec.Id === tooth.SpecificationId);

		return currentSpecWithMat ? currentSpecWithMat.Materials.some(mat => mat === value) : false;
	}

	private validateBody(tooth: Tooth): boolean {
		// validation requires the tooth to have specificationId and MaterialID. Make sure they have been copied or defined
		// CustomLabMaterialGroup is needed in case of custom materials, when custom materials weren't loaded
		const matOption = this.getMaterialSpecOption(
			tooth.MaterialID as any,
			tooth.SpecificationId as any,
			tooth.CustomLabMaterialGroup ?? undefined
		);

		return matOption?.ShadeVisible ?? false;
	}

	@memoize({
		extractUniqueId: (materialId: number | string, specId: number | string, materialGroup?: string) =>
			`${specId},${materialId}},${materialGroup}`
	})
	private getMaterialSpecOption(
		materialId: number | string,
		specId: number | string,
		materialGroup?: string
	): MaterialOption | undefined {
		return this.rxMaterialRulesService.getMaterialSpecOption(materialId, specId, materialGroup);
	}

	@memoize({
		extractUniqueId: (pmToCheck: ProcedureMapCheckedValue, toothInBridgeUnitType: UnitTypesInBridge) =>
			`${pmToCheck?.Id},${pmToCheck?.ProcedureId},${pmToCheck?.TypeId},${toothInBridgeUnitType}`
	})
	private getSpecIdsWithMaterialIdsForBridge(pmToCheck: Maybe<ProcedureMapCheckedValue>, toothInBridgeUnitType: UnitTypesInBridge) {
		return this.rxMaterialRulesService.getSpecIdsWithMaterialIdsForBridge(pmToCheck, toothInBridgeUnitType);
	}
}
